import React from 'react';

import classNames from 'classnames';
import CheckIconAmber from 'icons/icon-check-amber.svg';
import CheckIconGreen from 'icons/icon-check-green.svg';
import CheckIconGrey from 'icons/icon-check-grey.svg';
import ExclamationIconGrey from 'icons/icon-exclamation-grey.svg';
import ExclamationIconRed from 'icons/icon-exclamation-red.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CircleQuestion from 'components/application/CircleQuestion';
import RowBar from 'components/application/RowBar';
import Tooltip from 'components/application/Tooltip';

import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import PersonnelTrainingDownload from 'components/training/PersonnelTrainingDownload';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function RoleTrainingBar({
  isArchived,
  isDownloadingReport,
  isLoading = false,
  name,
  onDownloadClick,
  onTrainingEvidenceProcessed,
  personnelId,
  role,
  roleHasCourses = true,
  training,
  trainingEvidenceProcessed,
  trainingStatusReport,
}) {
  const { isViewingOwnProfile } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.RoleTrainingBar',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { courses, status } = trainingStatusReport;

  const shouldTruncateRoleName = role && role.attributes.position.length > 150;

  const statusTooltip = (courses, status) => {
    if (!courses.length) return '';

    const { name } = courses[0].attributes;

    const otherCourseCount = courses.length - 1;

    switch (courses.length) {
      case 1:
        return i18nT(`content.${status}.tooltip.default`, {
          interpolation: { escapeValue: false },
          name,
        });
      default:
        return i18nT(`content.${status}.tooltip.otherCourses`, {
          count: otherCourseCount,
          interpolation: { escapeValue: false },
          name,
        });
    }
  };

  const activeContentBanner = (() => {
    switch (status) {
      case 'noRequirements':
        return (
          <RoleTrainingBanner>
            <div className='tw-flex tw-items-center'>
              <CheckIconGrey />
              <span className='tw-ml-3'>
                {isViewingOwnProfile ?
                  i18nTCommon('training.dontHaveAnyRequiredTraining')
                : i18nT('content.noRequirements.default', {
                    interpolation: { escapeValue: false },
                    name,
                  })
                }
              </span>
            </div>
          </RoleTrainingBanner>
        );

      case 'expiring':
        return (
          <RoleTrainingBanner>
            <div className='tw-flex tw-items-center'>
              <CheckIconAmber />
              <span
                className={classNames(
                  'truncated-text-container__two-lines tw-mx-2 tw-max-w-[545px]',
                  shouldTruncateRoleName &&
                    'truncated-text-container__two-lines-overflow',
                )}
              >
                {isViewingOwnProfile ?
                  i18nT('content.expiring.ownProfile')
                : i18nT('content.expiring.default', {
                    interpolation: { escapeValue: false },
                    name,
                  })
                }
              </span>
              <Tooltip
                placement='top'
                tooltip={statusTooltip(courses, status)}
                trigger='hover'
              >
                <CircleQuestion transparent={true} />
              </Tooltip>
            </div>
          </RoleTrainingBanner>
        );

      case 'invalid':
        return (
          <RoleTrainingBanner color='amber'>
            <div className='tw-flex tw-items-center'>
              <ExclamationIconRed />
              <span
                className={classNames(
                  'truncated-text-container__two-lines tw-mx-2 tw-max-w-[545px]',
                  shouldTruncateRoleName &&
                    'truncated-text-container__two-lines-overflow',
                )}
              >
                {isViewingOwnProfile ?
                  i18nT('content.invalid.ownProfile')
                : i18nT('content.invalid.default', {
                    interpolation: { escapeValue: false },
                    name,
                  })
                }
              </span>
              <Tooltip
                placement='top'
                tooltip={statusTooltip(courses, status)}
                trigger='hover'
              >
                <CircleQuestion transparent={true} />
              </Tooltip>
            </div>
          </RoleTrainingBanner>
        );

      case 'valid':
        return (
          <RoleTrainingBanner>
            <div className='tw-flex tw-items-center'>
              <CheckIconGreen />
              <span
                className={classNames(
                  'truncated-text-container__two-lines tw-mx-2 tw-max-w-[545px]',
                  shouldTruncateRoleName &&
                    'truncated-text-container__two-lines-overflow',
                )}
              >
                {isViewingOwnProfile ?
                  i18nT('content.valid.ownProfile')
                : i18nT('content.valid.default', {
                    interpolation: { escapeValue: false },
                    name,
                  })
                }
              </span>
              <Tooltip
                placement='top'
                tooltip={i18nT('content.valid.tooltip')}
                trigger='hover'
              >
                <CircleQuestion />
              </Tooltip>
            </div>
          </RoleTrainingBanner>
        );
    }
  })();

  const archivedContentBanner = (() => (
    <RoleTrainingBanner>
      <div className='tw-flex tw-items-center'>
        <ExclamationIconGrey />
        <span className='tw-ml-3'>
          {i18nT('content.archived', {
            interpolation: { escapeValue: false },
            name,
          })}
        </span>
      </div>
    </RoleTrainingBanner>
  ))();

  let disabledTooltip;
  if (isArchived) {
    disabledTooltip = i18nT('disabledTooltip.isArchived');
  } else if (
    status === 'noRequirements' &&
    training.length === 0 &&
    !roleHasCourses
  ) {
    disabledTooltip = i18nTCommon('shared.noRecordsToDownload');
  } else if (!trainingEvidenceProcessed) {
    disabledTooltip = i18nTCommon('shared.currentlyUploadingEvidence');
  } else if (isDownloadingReport) {
    disabledTooltip = i18nT('disabledTooltip.anotherReportBeingGenerated');
  }

  const contentBanner =
    isArchived ? archivedContentBanner : activeContentBanner;
  const shouldShowContentBanner = roleHasCourses || isArchived;

  return (
    <>
      {shouldShowContentBanner && contentBanner}
      <RowBar
        actions={
          <div className='tooltip-parent'>
            <PersonnelTrainingDownload
              isDisabled={!!disabledTooltip}
              onDownloadClick={onDownloadClick}
              onTrainingEvidenceProcessed={onTrainingEvidenceProcessed}
              personnelId={personnelId}
              roleId={role ? role.id : null}
              trainingEvidenceProcessed={trainingEvidenceProcessed}
            />
            {disabledTooltip && (
              <Tooltip
                placement='top'
                tooltip={disabledTooltip}
                trigger='hover'
              />
            )}
          </div>
        }
        additionalClasses='tw-gap-4'
        content={
          !isLoading && (
            <>
              <span className='tw-mr-2 tw-text-l tw-font-semibold'>
                {roleHasCourses ?
                  i18nTCommon('training.roleSpecificTraining')
                : i18nTCommon('training.noRoleSpecificTraining')}
              </span>
              {!roleHasCourses && (
                <Tooltip
                  placement='top'
                  tooltip={i18nTCommon(
                    'training.noRoleSpecificTrainingTooltip',
                  )}
                  trigger='hover'
                >
                  <CircleQuestion />
                </Tooltip>
              )}
            </>
          )
        }
        modifiers={
          !isLoading && roleHasCourses ?
            ['large', 'border-bottom-none']
          : ['large']
        }
      />
    </>
  );
}

const colorClassName = {
  amber: 'tw-bg-amber-025',
  white: 'tw-bg-white',
};

function RoleTrainingBanner({ children, color = 'white' }) {
  return (
    <div
      className={classNames(
        'tw-m-0 tw-mb-5 tw-mt-4 tw-flex tw-rounded-lg tw-p-2',
        colorClassName[color],
        color === 'white' ?
          'tw-border-1 tw-border-solid tw-border-grey-100'
        : 'tw-border-0',
      )}
    >
      {children}
    </div>
  );
}

RoleTrainingBar.propTypes = {
  isArchived: PropTypes.bool.isRequired,
  isDownloadingReport: PropTypes.bool,
  isLoading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onDownloadClick: PropTypes.func,
  onTrainingEvidenceProcessed: PropTypes.func,
  personnelId: PropTypes.string.isRequired,
  role: PropTypes.object,
  roleHasCourses: PropTypes.bool,
  training: PropTypes.arrayOf(resourceShape('training')).isRequired,
  trainingEvidenceProcessed: PropTypes.bool,
  trainingStatusReport: PropTypes.shape({
    courses: PropTypes.arrayOf(resourceShape('courses')),
    status: PropTypes.string.isRequired,
  }).isRequired,
};

RoleTrainingBanner.propTypes = {
  children: PropTypes.node,
  color: PropTypes.oneOf(['white', 'amber']).isRequired,
};
